import { unpromisify, sleep } from "@mdotm/shadcn/legacy/utils";
import { type DataAttributesProps, IconButton } from "@mdotm/shadcn/mdotm/components";
import { Transition, defaultTailwindTransitionDurationMs } from "@mdotm/shadcn/legacy/components";
import { useAsync, type MaybePromise } from "@mdotm/utils/promise";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import type { Set } from "immutable";
import { type MouseEvent, useState } from "react";

export const CommonItemActions = {
	Copy({ disabled, onCopied, text }: { disabled?: boolean; onCopied(): void; text: string }): React.JSX.Element {
		const [pulse, setPulse] = useState(false);
		const [run, asyncState] = useAsync(async () => {
			setPulse(true);
			await Promise.all([
				window.navigator.clipboard.writeText(text).catch(console.error),
				sleep(defaultTailwindTransitionDurationMs),
			]);
			setPulse(false);
			await sleep(defaultTailwindTransitionDurationMs);
			onCopied();
		});
		return (
			<Transition
				in={pulse}
				className="transition-transform"
				enterFromClassName="scale-100"
				enterToClassName="scale-125"
			>
				{({ className }) => (
					<IconButton
						unstyled
						disabled={disabled || asyncState.kind === "loading"}
						className={className}
						onClick={unpromisify(() => run())}
						icon="Content-Copy"
						size={21}
						color={disabled ? themeCSSVars.palette_N100 : themeCSSVars.palette_P500}
					/>
				)}
			</Transition>
		);
	},
	Edit({
		disabled,
		onClick,
		onClickAsync,
		...dataAttributes
	}: { disabled?: boolean } & (
		| {
				onClick(e: MouseEvent): void;
				onClickAsync?: undefined;
		  }
		| {
				onClick?: undefined;
				onClickAsync(e: MouseEvent): MaybePromise<void>;
		  }
	) &
		DataAttributesProps): React.JSX.Element {
		return onClick ? (
			// `unstyled` on this branch and not the next: legacy's sync branch was a `<Button unstyled>`,
			// the async one a `TinyIconButton` with the pill.
			<IconButton
				unstyled
				disabled={disabled}
				onClick={onClick}
				{...dataAttributes}
				icon="Edit"
				size={21}
				color={disabled ? themeCSSVars.palette_N100 : themeCSSVars.palette_P500}
			/>
		) : (
			<IconButton
				icon="Edit"
				size={21}
				disabled={disabled}
				onClickAsync={onClickAsync}
				{...dataAttributes}
				color={disabled ? themeCSSVars.palette_N100 : themeCSSVars.palette_P500}
			/>
		);
	},
	Settings({ disabled, onClick }: { disabled?: boolean; onClick(e: MouseEvent): void }): React.JSX.Element {
		return (
			<IconButton
				unstyled
				disabled={disabled}
				onClick={onClick}
				icon="Settings"
				size={21}
				color={disabled ? themeCSSVars.palette_N100 : themeCSSVars.palette_P500}
			/>
		);
	},
	DeleteRestore<T>({
		disabled,
		deleted,
		item,
		onDeletedChange,
		...dataAttributes
	}: {
		disabled?: boolean;
		deleted: Set<T>;
		item: T;
		onDeletedChange(newDeleted: Set<T>): void;
	} & DataAttributesProps): React.JSX.Element {
		return (
			<IconButton
				unstyled
				disabled={disabled}
				onClick={(e) => {
					e.stopPropagation();
					onDeletedChange(!deleted.has(item) ? deleted.add(item) : deleted.remove(item));
				}}
				{...dataAttributes}
				icon={!deleted.has(item) ? "Delete" : "restore"}
				color={disabled ? themeCSSVars.palette_N100 : themeCSSVars.Button_bg_primary}
				size={21}
			/>
		);
	},
	Delete({
		disabled,
		onDelete,
		...dataAttributes
	}: {
		disabled?: boolean;
		onDelete(): void;
	} & DataAttributesProps): React.JSX.Element {
		return (
			<IconButton
				unstyled
				disabled={disabled}
				onClick={(e) => {
					e.stopPropagation();
					onDelete();
				}}
				{...dataAttributes}
				icon="Delete"
				color={disabled ? themeCSSVars.palette_N100 : themeCSSVars.Button_bg_primary}
				size={21}
			/>
		);
	},
};
